import React, { useEffect, useState } from 'react'

import { useNavigation } from '@zet/common/hooks/useNavigation'
import { colors as zetColors } from '@zet/design-system'
import { Button } from '@zet/design-system/button'
import { Icon } from '@zet/design-system/icons'
import { Stack, XStack } from '@zet/design-system/layout'
import { B5, BTN, SH3 } from '@zet/design-system/typography'
import useBrandStore from '@zet/feature-brands/store/brands'
import { useUserContext } from '@zet/feature-user'
import { i18n } from '@zet/localization'
import { LeadStatus } from '@zet/utils/Constants'
import { LoggedInNavigation } from '@zet/utils/interface'
import _ from 'lodash'

import { LeadDetailsType } from '../types'
import ActionRequired from './actionRequired'

type ApplicationInProgressProps = {
  item?: LeadDetailsType
  videoId?: string
  brandName?: string
  inactive?: boolean
  brandComms?: object
}

const ApplicationInProgress = ({
  item,
  videoId,
  brandName,
  inactive,
  brandComms,
}: ApplicationInProgressProps) => {
  const { navigate } = useNavigation<LoggedInNavigation>()
  const [link, setLink] = useState('')
  const [label, setLabel] = useState('Link')
  const linkLabel = label || 'Link'
  const status = item?.status
  const video = videoId
  const appAsks = item?.applicationAsks
  const asks = _.orderBy(appAsks, ['isResolved'], ['desc'])
  const latestAskRemark = asks?.[asks.length - 1]?.remarks ?? ''
  const partnerId = item?.partnerId
  const customerId = item?.customerId
  const customerPhoneNumber = item?.phoneNumber
  const { data: user } = useUserContext()
  const { setLeadSource } = useBrandStore()

  const videoMetaData = item?.applicationAsks?.find(
    (ask) => ask?.showYoutubeVideo
  )
  const videoData =
    brandComms?.leadVideoUrl?.[
      videoMetaData?.askCategoryCode as keyof typeof videoMetaData
    ]?.[videoMetaData?.askCode as keyof typeof videoMetaData] ??
    brandComms?.leadVideoUrl?.[
      videoMetaData?.askCategoryCode as keyof typeof videoMetaData
    ]?.defaultVideo

  useEffect(() => {
    item?.applicationAsks?.map((share) => {
      setLink(share?.appLink?.value)
      setLabel(share?.appLink?.label)
    })
  }, [setLabel, setLink])

  const actionRequiredAsk = (asks) => {
    if (asks.length - 1 == 0) {
      return _.map(asks, (ocAsk) => (
        <ActionRequired ocAsk={ocAsk} item={item} videoData={videoData} />
      ))
    } else {
      const arAsks = [asks[asks.length - 2], asks[asks.length - 1]]
      return _.map(arAsks, (ocAsk) => (
        <ActionRequired ocAsk={ocAsk} item={item} videoData={videoData} />
      ))
    }
  }

  const getShareMessage = (isWithLink = false) => {
    return {
      en: `${
        videoMetaData?.showYoutubeVideo
          ? `Watch this short video for error-free Video-KYC\n${
              videoData?.[i18n.locale]?.url
            }\n\n`
          : ''
      }Hi ${
        item?.name
      },\n\nYou are just a few steps away from completing your application for ${brandName}. ${
        latestAskRemark && `\n\n${latestAskRemark}\n`
      }Complete now using this link:\n\n${
        isWithLink
          ? `${linkLabel}\n${link}\n\n{{agentName}}\nBusiness Consultant`
          : `{{URL}}\n\n{{agentName}}\nBusiness Consultant`
      }`,
      hi: `${
        videoMetaData?.showYoutubeVideo
          ? `पहली बार में सफलतापूर्वक वीडियो-KYC पूरा करने के लिए ये वीडियो जरूर देखें\n${
              videoData?.[i18n.locale]?.url
            }\n\n`
          : ''
      }हेलो ${
        item?.name
      },\n\n${brandName} के आवेदन को पूरा करने के लिए कुछ ही स्टेप्स बचे हैं। ${
        latestAskRemark && `\n\n${latestAskRemark}`
      }\nनीचे दी गई लिंक के ज़रिए आवेदन पूरा करें:\n\n${
        isWithLink
          ? `${linkLabel}\n${link}\n\n{{agentName}}\nबिज़नेस कंसलटेंट`
          : `{{URL}}\n\n{{agentName}}\nबिज़नेस कंसलटेंट`
      } `,
    }
  }

  if (status == LeadStatus.ACTION_REQUIRED) {
    return (
      <Stack>
        <Stack className="border-secondary-100 bg-neutral-white100 mt-2  rounded-sm border-l-4">
          <XStack className="py-4/2 px-3">
            <Stack className="bg-secondary-100 absolute left-[30] h-4 w-1" />
            <Stack className="bg-secondary-100 absolute left-3 top-3 items-center rounded-lg">
              <Icon
                type={'DocumentOutlined'}
                size={20}
                color={zetColors.neutral['white100']}
                className="m-1.5"
              />
            </Stack>
            <Stack className="left-6 mt-2 justify-center">
              <SH3 className="  text-grays-80">
                {i18n.t('leads.applicationInProgress')}
              </SH3>
            </Stack>
          </XStack>

          {actionRequiredAsk(asks)}

          {video && !videoMetaData?.showYoutubeVideo ? (
            <Stack className="mx-3 my-2">
              <Button
                fullWidth
                intent
                size={'sm'}
                className={'px-4/2 items-center justify-center py-2'}
                onPress={() =>
                  navigate('ApplicationProcess', { brandId: partnerId })
                }
                eventName="click_action"
                eventPayload={{
                  item_name: 'lead_page_learn_about_application_process',
                  lead_id: item?.id,
                  lead_status: status,
                  user_id: user?.id,
                  customer_id: customerId,
                  partner_id: partnerId,
                  brand_name: brandName,
                }}
              >
                <XStack className="items-center">
                  <Icon
                    type="VideoOutlined"
                    size={20}
                    color={zetColors.primary[50]}
                    className={'mr-2'}
                  />
                  <BTN className="text-primary-100 text-center">
                    {i18n.t('leads.videoButton')}
                  </BTN>
                </XStack>
              </Button>
            </Stack>
          ) : null}

          {item?.hasNoUpdate ? (
            <Stack className="bg-grays-20 mx-3 my-2 rounded">
              <XStack className="p-1">
                <Icon
                  type={'ErrorOutlined'}
                  size={20}
                  color={zetColors.warning[150]}
                />
                <B5 className="text-grays-60 flex-1 px-2">
                  {i18n.t('leads.hasNoUpdate')}
                </B5>
              </XStack>
            </Stack>
          ) : null}

          {link ? (
            <Stack className="mb-4/2 mx-3">
              <Button
                fullWidth
                size={'md'}
                className={
                  'bg-success-100 items-center justify-center px-3 py-2'
                }
                onPress={() => {
                  setLeadSource(item?.source)
                  navigate('ShareCardLink', {
                    brandId: partnerId,
                    customerName: item?.name,
                    customerPhoneNumber: customerPhoneNumber,
                    customerId: customerId,
                    reopen: false,
                    shareButtonMessage: getShareMessage(true),
                    routeName: 'forCustomerFlow',
                    eventName: 'click_action',
                    shareImage: videoData?.[i18n.locale]?.thumbnail,
                    eventPayload: {
                      item_name: 'lead_page_share_message_preview_screen_share',
                      lead_id: item?.id,
                      lead_status: status,
                      user_id: user?.id,
                      customer_id: customerId,
                      partner_id: partnerId,
                      brand_name: brandName,
                    },
                  })
                }}
                eventName="click_action"
                eventPayload={{
                  item_name: 'lead_page_share_ask_link',
                  lead_id: item?.id,
                  lead_status: status,
                  user_id: user?.id,
                  customer_id: customerId,
                  partner_id: partnerId,
                  brand_name: brandName,
                }}
              >
                <XStack className="items-center">
                  <Icon
                    type="LinkOutlined"
                    size={24}
                    color={zetColors.neutral['white100']}
                    className="mr-2"
                  />
                  <BTN className="text-neutral-white100 text-center">
                    {label
                      ? i18n.t('leads.shareKyc', { vkycLink: label })
                      : i18n.t('leads.shareKyc', { vkycLink: 'Link' })}
                  </BTN>
                </XStack>
              </Button>
            </Stack>
          ) : (
            <Stack className="mb-4/2 mx-3">
              <Button
                disabled={inactive}
                fullWidth
                size={'md'}
                className={' px-3 py-2'}
                onPress={() => {
                  setLeadSource(item?.source)
                  navigate('ShareCardLink', {
                    brandId: partnerId,
                    customerName: item?.name,
                    customerPhoneNumber: customerPhoneNumber,
                    customerId: customerId,
                    shareButtonMessage: getShareMessage(),
                    routeName: 'forCustomerFlow',
                    shareImage: videoData?.[i18n.locale]?.thumbnail,
                    eventName: 'click_action',
                    eventPayload: {
                      item_name: 'lead_page_share_message_preview_screen_share',
                      lead_id: item?.id,
                      lead_status: status,
                      customer_id: customerId,
                      partner_id: partnerId,
                      brand_name: brandName,
                    },
                  })
                }}
                eventName="click_action"
                eventPayload={{
                  item_name: 'lead_page_send_reminder',
                  lead_id: item?.id,
                  lead_status: status,
                  customer_id: customerId,
                  partner_id: partnerId,
                  brand_name: brandName,
                }}
              >
                {inactive ? (
                  <XStack className="items-center">
                    <BTN className=" text-neutral-white100">
                      {i18n.t('leads.inactiveShare')}
                    </BTN>
                  </XStack>
                ) : (
                  <XStack className="items-center">
                    <Icon
                      type="MessageOutlined"
                      size={24}
                      color={zetColors.neutral['white100']}
                      className={'mr-2'}
                    />
                    <BTN className=" text-neutral-white100">
                      {i18n.t('leads.sendRemainder')}
                    </BTN>
                  </XStack>
                )}
              </Button>
            </Stack>
          )}
        </Stack>
      </Stack>
    )
  } else {
    return (
      <XStack className="border-grays-30 bg-neutral-white100 py-4/2 mt-2 rounded-sm border-l-4 px-3">
        <Stack className="bg-grays-30 absolute left-[30] h-4 w-1" />
        <Stack className="bg-grays-30 absolute left-3 top-3 items-center rounded-lg">
          <Icon
            type={'DocumentOutlined'}
            size={20}
            color={zetColors.grays[50]}
            className="m-1.5"
          />
        </Stack>
        <Stack className="left-6">
          <SH3 className=" text-grays-80">
            {i18n.t('leads.applicationInProgress')}
          </SH3>
          <B5 className="text-grays-50">{i18n.t('leads.inPorgressDesc')}</B5>
        </Stack>
      </XStack>
    )
  }
}

export default ApplicationInProgress
